'use client';

import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';

import {
  type TenantAnalyticsDeliveryResult,
  type TenantAnalyticsEvent,
  type TenantAnalyticsRuntime,
  createTenantAnalyticsRuntime,
} from '@kit/analytics/tenant';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import type { TenantVerifiedPurchase } from '~/(tenant)/_lib/analytics/tenant-purchase.types';
import {
  useIosTrackingStatus,
  useIosTrackingSupport,
} from '~/(tenant)/_lib/capacitor/app-tracking-transparency';
import { useCapacitor } from '~/(tenant)/_lib/capacitor/use-capacitor';

import { useTenantAnalyticsConsent } from '../tenant-analytics-consent-provider';
import { TenantAnalyticsConsentPreferences } from './tenant-analytics-consent-preferences';
import {
  resolveAppTrackingConsent,
  shouldAppTrackingGovernConsent,
} from './tenant-app-tracking-consent';
import {
  type TenantAnalyticsClientConfiguration,
  type TenantGoogleTransport,
  activateTenantGoogleTransport,
  createTenantAnalyticsEventId,
  createTenantAnalyticsInitialReferrer,
  createTenantAnalyticsPagePath,
  createTenantAnalyticsSpaReferrer,
  createTenantGoogleBrowserAdapters,
  denyTenantGoogleTransport,
  getConsentGatedTenantGoogleScriptDescriptor,
  hasRequiredTenantGoogleConsent,
  isTenantAnalyticsBrowserDocumentLocationSafe,
  isTenantGoogleTransportActivated,
  isTenantGoogleTransportReady,
  selectTenantGoogleTransport,
  setTenantGoogleTransportReady,
  subscribeToTenantGoogleTransportActivation,
} from './tenant-google-browser';
import {
  type TenantPixelTransport,
  activateTenantPixelTransports,
  areTenantPixelTransportsActivated,
  areTenantPixelTransportsReady,
  createTenantPixelBrowserAdapters,
  getConsentGatedTenantPixelScripts,
  hasRequiredTenantPixelConsent,
  revokeTenantPixelTransports,
  selectTenantPixelTransports,
  setTenantPixelTransportReady,
  subscribeToTenantPixelActivation,
  tenantPixelTransportsRequireHardReload,
} from './tenant-pixels-browser';

export type TenantViewContentInput = {
  readonly contentId: string;
  readonly contentType: string;
  readonly contentName?: string;
} & (
  | {
      readonly value: number;
      readonly currency: string;
    }
  | {
      readonly value?: never;
      readonly currency?: never;
    }
);

interface TenantPageViewInput {
  readonly path: string;
  readonly browserDocumentLocationSafe: boolean;
  readonly title?: string;
  readonly referrer?: string;
}

export type TenantBeginCheckoutInput = TenantViewContentInput;

interface TenantAnalyticsContextValue {
  readonly enabled: boolean;
  readonly deliveryReady: boolean;
  readonly provider: TenantGoogleTransport['provider'] | null;
  readonly transportKey: string | null;
  trackViewContent(
    input: TenantViewContentInput,
  ): Promise<readonly TenantAnalyticsDeliveryResult[]>;
  trackBeginCheckout(
    input: TenantBeginCheckoutInput,
  ): Promise<readonly TenantAnalyticsDeliveryResult[]>;
  trackPageView(
    input: TenantPageViewInput,
  ): Promise<readonly TenantAnalyticsDeliveryResult[]>;
  trackPurchase(
    purchase: TenantVerifiedPurchase,
  ): Promise<readonly TenantAnalyticsDeliveryResult[]>;
}

const TenantAnalyticsContext =
  createContext<TenantAnalyticsContextValue | null>(null);
const EMPTY_DELIVERY_RESULTS: readonly TenantAnalyticsDeliveryResult[] =
  Object.freeze([]);

export function TenantAnalyticsProvider({
  children,
  configuration,
  tenantId,
}: React.PropsWithChildren<{
  tenantId: string;
  configuration: TenantAnalyticsClientConfiguration;
}>) {
  const {
    consent: storedConsent,
    hasDecision,
    rejectNonEssential,
    updateConsent,
  } = useTenantAnalyticsConsent();
  const pageOrigin = useBrowserPageOrigin();
  const transport = useMemo(
    () => selectTenantGoogleTransport(tenantId, configuration),
    [configuration, tenantId],
  );
  const pixelTransports = useMemo(
    () => selectTenantPixelTransports(tenantId, configuration),
    [configuration, tenantId],
  );
  // The advertising category is only meaningful when a marketing tag can
  // actually fire; a GA4-only tenant must not be offered a toggle for it.
  const offersMarketing =
    transport?.provider === 'google_tag_manager' ||
    (transport?.provider === 'google_direct' && transport.googleAds !== null) ||
    pixelTransports.length > 0;
  const iosTrackingSupport = useIosTrackingSupport();
  const iosTrackingStatus = useIosTrackingStatus(offersMarketing);
  const appTrackingGovernsConsent = shouldAppTrackingGovernConsent(
    offersMarketing,
    iosTrackingSupport,
  );
  const consent = useMemo(
    () =>
      resolveAppTrackingConsent(
        storedConsent,
        offersMarketing,
        iosTrackingSupport,
        iosTrackingStatus,
      ),
    [iosTrackingStatus, iosTrackingSupport, offersMarketing, storedConsent],
  );
  const pixelHardReloadRequired =
    tenantPixelTransportsRequireHardReload(pixelTransports);
  const runtime = useMemo(
    () =>
      createBrowserRuntime(
        tenantId,
        configuration.environment,
        transport,
        pixelHardReloadRequired ? [] : pixelTransports,
        pageOrigin,
      ),
    [
      configuration.environment,
      pageOrigin,
      pixelHardReloadRequired,
      pixelTransports,
      tenantId,
      transport,
    ],
  );
  const isTransportActivated = useTenantGoogleTransportActivation(transport);
  const isTransportReady = useTenantGoogleTransportReadiness(transport);
  const pixelsActivated = useTenantPixelActivation(pixelTransports);
  const pixelsReady = useTenantPixelReadiness(pixelTransports);
  const previousTransportRef = useRef<TenantGoogleTransport | null>(null);

  // One activation entry point for every transport kind; safe to call
  // repeatedly (each underlying activation is idempotent per key).
  const activateConsentedTransports = useCallback(() => {
    if (transport && hasRequiredTenantGoogleConsent(transport, consent)) {
      activateTenantGoogleTransport(transport, consent);
    }

    if (!pixelHardReloadRequired && pixelTransports.length > 0) {
      activateTenantPixelTransports(pixelTransports, consent);
    }
  }, [consent, pixelHardReloadRequired, pixelTransports, transport]);

  useEffect(() => {
    if (pixelHardReloadRequired) {
      window.location.reload();
      return;
    }

    const previousTransport = previousTransportRef.current;

    if (previousTransport && previousTransport.key !== transport?.key) {
      denyTenantGoogleTransport(previousTransport);
    }

    previousTransportRef.current = transport;

    if (transport && !hasRequiredTenantGoogleConsent(transport, consent)) {
      denyTenantGoogleTransport(transport);
    }

    if (pixelTransports.length > 0 && !hasRequiredTenantPixelConsent(consent)) {
      revokeTenantPixelTransports(pixelTransports);
    }

    activateConsentedTransports();
  }, [
    activateConsentedTransports,
    consent,
    pixelHardReloadRequired,
    pixelTransports,
    transport,
  ]);

  useEffect(() => {
    return () => {
      if (transport) {
        denyTenantGoogleTransport(transport);
      }

      revokeTenantPixelTransports(pixelTransports);
    };
  }, [pixelTransports, transport]);

  const googleIsEligible =
    transport !== null && hasRequiredTenantGoogleConsent(transport, consent);
  const pixelsAreEligible =
    !pixelHardReloadRequired &&
    pixelTransports.length > 0 &&
    hasRequiredTenantPixelConsent(consent);
  const hasConsentedDelivery = googleIsEligible || pixelsAreEligible;
  const deliveryReady =
    hasConsentedDelivery &&
    (!googleIsEligible || isTransportReady) &&
    (!pixelsAreEligible || pixelsReady);

  const trackPageView = useCallback(
    async (input: TenantPageViewInput) => {
      if (!runtime || !hasConsentedDelivery) {
        return EMPTY_DELIVERY_RESULTS;
      }

      activateConsentedTransports();

      return runtime.track(
        {
          eventId: createTenantAnalyticsEventId(),
          name: 'page_view',
          occurredAt: new Date().toISOString(),
          path: input.path,
          browserDocumentLocationSafe: input.browserDocumentLocationSafe,
          ...(input.title ? { title: input.title } : {}),
          ...(input.referrer ? { referrer: input.referrer } : {}),
        },
        consent,
      );
    },
    [activateConsentedTransports, consent, hasConsentedDelivery, runtime],
  );

  const trackContentEvent = useCallback(
    async (
      name: 'view_content' | 'begin_checkout',
      input: TenantViewContentInput,
    ) => {
      if (!runtime || !hasConsentedDelivery) {
        return EMPTY_DELIVERY_RESULTS;
      }

      activateConsentedTransports();

      const eventBase = {
        eventId: createTenantAnalyticsEventId(),
        occurredAt: new Date().toISOString(),
        browserDocumentLocationSafe: getBrowserDocumentLocationSafety(),
        contentId: input.contentId,
        contentType: input.contentType,
        ...(input.contentName ? { contentName: input.contentName } : {}),
      };

      if (input.value !== undefined) {
        return runtime.track(
          {
            ...eventBase,
            name,
            value: input.value,
            currency: input.currency,
          } as TenantAnalyticsEvent,
          consent,
        );
      }

      return runtime.track(
        {
          ...eventBase,
          name,
        } as TenantAnalyticsEvent,
        consent,
      );
    },
    [activateConsentedTransports, consent, hasConsentedDelivery, runtime],
  );

  const trackViewContent = useCallback(
    (input: TenantViewContentInput) => trackContentEvent('view_content', input),
    [trackContentEvent],
  );

  const trackBeginCheckout = useCallback(
    (input: TenantBeginCheckoutInput) =>
      trackContentEvent('begin_checkout', input),
    [trackContentEvent],
  );

  const trackPurchase = useCallback(
    async (purchase: TenantVerifiedPurchase) => {
      if (!runtime || !hasConsentedDelivery) {
        return EMPTY_DELIVERY_RESULTS;
      }

      activateConsentedTransports();

      // The identity is server-derived and deterministic — never regenerated
      // here — so refreshes and retries reuse the same eventId/transactionId
      // and providers deduplicate instead of double counting.
      return runtime.track(
        {
          eventId: purchase.eventId,
          name: 'purchase',
          occurredAt: purchase.occurredAt,
          browserDocumentLocationSafe: getBrowserDocumentLocationSafety(),
          transactionId: purchase.transactionId,
          value: purchase.value,
          currency: purchase.currency,
          items: purchase.items,
        },
        consent,
      );
    },
    [activateConsentedTransports, consent, hasConsentedDelivery, runtime],
  );

  const enabled = runtime !== null && hasConsentedDelivery;
  // A stable identity for session-level dedup keys, covering whichever
  // transports are configured (Google, pixels, or both).
  const transportKey =
    [
      ...(transport ? [transport.key] : []),
      ...pixelTransports.map((pixel) => pixel.key),
    ].join('|') || null;
  const contextValue = useMemo<TenantAnalyticsContextValue>(
    () => ({
      deliveryReady,
      enabled,
      provider: transport?.provider ?? null,
      transportKey,
      trackBeginCheckout,
      trackPageView,
      trackPurchase,
      trackViewContent,
    }),
    [
      deliveryReady,
      enabled,
      trackBeginCheckout,
      trackPageView,
      trackPurchase,
      trackViewContent,
      transport,
      transportKey,
    ],
  );
  const script = getConsentGatedTenantGoogleScriptDescriptor(
    transport,
    consent,
    isTransportActivated && runtime !== null,
  );
  const pixelScripts = getConsentGatedTenantPixelScripts(
    pixelHardReloadRequired ? [] : pixelTransports,
    consent,
    pixelsActivated && runtime !== null,
  );
  const hasAnyTransport = transport !== null || pixelTransports.length > 0;
  const shouldPromptForConsent =
    pageOrigin !== null &&
    hasAnyTransport &&
    !hasDecision &&
    !appTrackingGovernsConsent;
  // An iOS build without the ATT plugin has nothing to manage: tracking is
  // off and cannot be switched on from the page.
  const offersPreferences =
    hasAnyTransport &&
    !(appTrackingGovernsConsent && iosTrackingSupport === 'unsupported');

  return (
    <TenantAnalyticsContext.Provider value={contextValue}>
      {script ? (
        <Script
          id={script.id}
          src={script.src}
          strategy="afterInteractive"
          onReady={() => {
            if (transport) {
              setTenantGoogleTransportReady(transport, true);
            }
          }}
          onError={() => {
            if (transport) {
              setTenantGoogleTransportReady(transport, false);
            }
          }}
        />
      ) : null}

      {pixelScripts.map((pixelScript) => (
        <Script
          id={pixelScript.id}
          key={pixelScript.id}
          src={pixelScript.src}
          strategy="afterInteractive"
          onReady={() => setTenantPixelTransportReady(pixelScript.key, true)}
          onError={() => setTenantPixelTransportReady(pixelScript.key, false)}
        />
      ))}

      <Suspense fallback={null}>
        <TenantPageViewTracker />
      </Suspense>

      {children}

      {shouldPromptForConsent ? (
        <TenantAnalyticsConsentPrompt
          onAcceptAnalytics={() =>
            updateConsent({ analytics: true, advertising: false })
          }
          onAcceptAnalyticsAndMarketing={() =>
            updateConsent({ analytics: true, advertising: true })
          }
          onReject={rejectNonEssential}
          offersMarketing={offersMarketing}
        />
      ) : null}

      {/*
        Mounted whenever the tenant has any tag, INCLUDING after a decision has
        been made — this is the withdrawal path (GDPR Art. 7(3)), so it must
        stay reachable once the banner is gone.
      */}
      {offersPreferences ? (
        <TenantAnalyticsConsentPreferences
          iosTracking={
            appTrackingGovernsConsent
              ? {
                  allowed: consent.advertising,
                  decided:
                    iosTrackingStatus !== null &&
                    iosTrackingStatus !== 'notDetermined',
                }
              : null
          }
          offersMarketing={offersMarketing}
        />
      ) : null}
    </TenantAnalyticsContext.Provider>
  );
}

export function useTenantAnalytics() {
  const context = useContext(TenantAnalyticsContext);

  if (!context) {
    throw new Error(
      'useTenantAnalytics must be used within TenantAnalyticsProvider',
    );
  }

  return context;
}

/**
 * Nullable variant for surfaces that must never crash when the provider is
 * absent (payment confirmation pages render outside the provider when the
 * request has no tenant). Returns null instead of throwing.
 */
export function useOptionalTenantAnalytics() {
  return useContext(TenantAnalyticsContext);
}

function TenantPageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const serializedSearchParameters = searchParams.toString();
  const { enabled, trackPageView, transportKey } = useTenantAnalytics();
  const lastPageViewKeyRef = useRef<string | null>(null);
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !pathname || !transportKey) {
      return;
    }

    const path = createTenantAnalyticsPagePath(
      pathname,
      serializedSearchParameters,
    );

    if (!path) {
      return;
    }

    const pageViewKey = `${transportKey}:${path}`;

    if (lastPageViewKeyRef.current === pageViewKey) {
      return;
    }

    const previousPath = previousPathRef.current;
    const referrer = resolveTenantPageReferrer(previousPath, path);
    lastPageViewKeyRef.current = pageViewKey;
    previousPathRef.current = path;

    void trackPageView({
      path,
      browserDocumentLocationSafe: isTenantAnalyticsBrowserDocumentLocationSafe(
        window.location.href,
        document.referrer,
      ),
      ...(document.title
        ? { title: normalizeOptionalText(document.title, 512) }
        : {}),
      ...(referrer ? { referrer } : {}),
    });
  }, [
    enabled,
    pathname,
    serializedSearchParameters,
    trackPageView,
    transportKey,
  ]);

  return null;
}

function TenantAnalyticsConsentPrompt({
  onAcceptAnalytics,
  onAcceptAnalyticsAndMarketing,
  onReject,
  offersMarketing,
}: {
  onAcceptAnalytics(): void;
  onAcceptAnalyticsAndMarketing(): void;
  onReject(): void;
  offersMarketing: boolean;
}) {
  // In the app shells the prompt docks to the bottom edge as a sheet with
  // full-width, primary-first buttons, instead of a floating web card.
  const { isNative } = useCapacitor();

  return (
    <aside
      aria-labelledby="tenant-analytics-consent-title"
      className={cn(
        'bg-background fixed z-[100] border shadow-xl',
        isNative
          ? 'inset-x-0 bottom-0 rounded-t-2xl border-x-0 border-b-0 px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]'
          : 'inset-x-4 bottom-4 mx-auto max-w-xl rounded-xl p-4',
      )}
      data-test="tenant-analytics-consent-banner"
      role="region"
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="font-medium" id="tenant-analytics-consent-title">
            <Trans
              i18nKey="common:tenantAnalyticsConsent.title"
              defaults="Optional analytics"
            />
          </p>

          <p className="text-muted-foreground text-sm">
            {offersMarketing ? (
              <Trans
                i18nKey="common:tenantAnalyticsConsent.marketingDescription"
                defaults="Allow this business to measure visits and use marketing tags. Your choice is kept separate for this business."
              />
            ) : (
              <Trans
                i18nKey="common:tenantAnalyticsConsent.description"
                defaults="Allow this business to measure page and content views. Your choice is kept separate for this business."
              />
            )}
          </p>
        </div>

        <div
          className={cn(
            'flex gap-2',
            isNative
              ? 'flex-col-reverse pt-1 [&>button]:h-11 [&>button]:w-full'
              : 'flex-wrap justify-end',
          )}
        >
          <Button
            data-test="tenant-analytics-consent-reject"
            onClick={onReject}
            type="button"
            variant="outline"
          >
            <Trans
              i18nKey="common:tenantAnalyticsConsent.reject"
              defaults="Reject"
            />
          </Button>

          <Button
            data-test="tenant-analytics-consent-accept"
            onClick={onAcceptAnalytics}
            type="button"
            variant={offersMarketing ? 'outline' : 'default'}
          >
            <Trans
              i18nKey="common:tenantAnalyticsConsent.acceptAnalytics"
              defaults="Allow analytics"
            />
          </Button>

          {offersMarketing ? (
            <Button
              data-test="tenant-analytics-consent-accept-marketing"
              onClick={onAcceptAnalyticsAndMarketing}
              type="button"
            >
              <Trans
                i18nKey="common:tenantAnalyticsConsent.acceptAnalyticsAndMarketing"
                defaults="Allow analytics & marketing"
              />
            </Button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

function createBrowserRuntime(
  tenantId: string,
  environment: TenantAnalyticsClientConfiguration['environment'],
  transport: TenantGoogleTransport | null,
  pixelTransports: readonly TenantPixelTransport[],
  pageOrigin: string | null,
): TenantAnalyticsRuntime | null {
  if (!environment || !pageOrigin) {
    return null;
  }

  try {
    const adapters = [
      ...(transport
        ? createTenantGoogleBrowserAdapters(transport, pageOrigin)
        : []),
      ...createTenantPixelBrowserAdapters(pixelTransports),
    ];

    if (adapters.length === 0) {
      return null;
    }

    return createTenantAnalyticsRuntime({
      tenantId,
      environment,
      enabled: true,
      adapters,
    });
  } catch {
    return null;
  }
}

function useBrowserPageOrigin() {
  return useSyncExternalStore(
    subscribeToBrowserOrigin,
    getBrowserOrigin,
    getServerOrigin,
  );
}

function useTenantPixelActivation(
  pixelTransports: readonly TenantPixelTransport[],
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      pixelTransports.length > 0
        ? subscribeToTenantPixelActivation(onStoreChange)
        : () => undefined,
    [pixelTransports],
  );
  const getSnapshot = useCallback(
    () => areTenantPixelTransportsActivated(pixelTransports),
    [pixelTransports],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerActivation);
}

function useTenantGoogleTransportActivation(
  transport: TenantGoogleTransport | null,
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      transport
        ? subscribeToTenantGoogleTransportActivation(transport, onStoreChange)
        : () => undefined,
    [transport],
  );
  const getSnapshot = useCallback(
    () => (transport ? isTenantGoogleTransportActivated(transport) : false),
    [transport],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerActivation);
}

function useTenantGoogleTransportReadiness(
  transport: TenantGoogleTransport | null,
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      transport
        ? subscribeToTenantGoogleTransportActivation(transport, onStoreChange)
        : () => undefined,
    [transport],
  );
  const getSnapshot = useCallback(
    () => (transport ? isTenantGoogleTransportReady(transport) : false),
    [transport],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerActivation);
}

function useTenantPixelReadiness(
  pixelTransports: readonly TenantPixelTransport[],
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      pixelTransports.length > 0
        ? subscribeToTenantPixelActivation(onStoreChange)
        : () => undefined,
    [pixelTransports],
  );
  const getSnapshot = useCallback(
    () => areTenantPixelTransportsReady(pixelTransports),
    [pixelTransports],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getServerActivation);
}

function subscribeToBrowserOrigin() {
  return () => undefined;
}

function getBrowserOrigin() {
  return window.location.origin;
}

function getServerOrigin() {
  return null;
}

function getServerActivation() {
  return false;
}

function normalizeOptionalText(value: string, maximumLength: number) {
  const normalized = value.trim();

  if (!normalized) {
    return undefined;
  }

  return normalized.slice(0, maximumLength);
}

function getBrowserDocumentLocationSafety() {
  return isTenantAnalyticsBrowserDocumentLocationSafe(
    window.location.href,
    document.referrer,
  );
}

function resolveTenantPageReferrer(
  previousPath: string | null,
  currentPath: string,
) {
  if (!previousPath) {
    return createTenantAnalyticsInitialReferrer(document.referrer);
  }

  if (previousPath === currentPath) {
    return null;
  }

  return createTenantAnalyticsSpaReferrer(previousPath, window.location.origin);
}
